import { el, clear } from '../../core/dom.js';
import { empty } from '../../components/empty.js';
import { errorPanel } from '../../components/error.js';
import { button } from '../../components/button.js';
import { skeleton } from '../../components/skeleton.js';
import { toast } from '../../components/toast.js';
import { PAGE_SIZE } from './model.js';
import { listSchedules, setEnabled } from './actions.js';
import { renderEmpty, renderHeader, renderList, renderSteps } from './list.js';
import { openScheduleEditor } from './form.js';

function handlersFor(ui) {
  return {
    onEdit: (row) => openEditor(ui, row),
    onToggle: (row) => toggle(ui, row),
    onMore: () => more(ui),
    get busyId() { return ui.state.busyId; },
  };
}

function paint(ui) {
  const { state, opts } = ui;
  clear(ui.head);
  clear(ui.main);
  ui.head.append(renderHeader(opts.productName, () => openEditor(ui, null)));
  if (state.loading) ui.main.append(skeleton(5));
  else if (state.fatal) ui.main.append(errorView(ui));
  else if (!state.rows.length) ui.main.append(emptyView(ui));
  else ui.main.append(renderList(state, ui.handlers));
  ui.live.textContent = state.live || '';
}

function errorView(ui) {
  return errorPanel({
    title: 'Schedules unavailable',
    message: ui.state.fatal,
    action: button('Try again', { variant: 'primary', onClick: () => load(ui, true) }),
  });
}

function emptyView(ui) {
  return el('div', { class: 'sched-stack' },
    renderEmpty(ui.opts.productName, () => openEditor(ui, null)),
    renderSteps(ui.opts.productId));
}

function openEditor(ui, row) {
  if (ui.state.editor) ui.state.editor.close();
  ui.state.editor = openScheduleEditor({
    row,
    me: ui.opts.me,
    productId: ui.opts.productId,
    productName: ui.opts.productName,
    saved: ui.state.saved,
    onClose() { ui.state.editor = null; },
    onSaved: () => load(ui, false),
  });
}

function more(ui) {
  ui.state.shown += PAGE_SIZE;
  const shown = Math.min(ui.state.shown, ui.state.rows.length);
  ui.state.live = `Showing ${shown} of ${ui.state.rows.length}`;
  paint(ui);
}

async function toggle(ui, row) {
  if (ui.state.busyId) return;
  ui.state.busyId = row.id;
  paint(ui);
  const enabled = row.enabled === false;
  const result = await setEnabled(row.id, enabled);
  if (ui.state.dead) return;
  ui.state.busyId = '';
  if (!result || result.error) {
    toast((result && result.error) || 'Could not update the schedule', 'error');
    paint(ui);
    return;
  }
  const idx = ui.state.rows.findIndex((item) => item.id === row.id);
  if (idx >= 0) ui.state.rows[idx] = result.schedule || { ...ui.state.rows[idx], enabled };
  ui.state.live = enabled ? 'Schedule resumed' : 'Schedule paused';
  toast(ui.state.live);
  paint(ui);
}

async function load(ui, initial) {
  if (initial) {
    ui.state.loading = true;
    ui.state.fatal = '';
    paint(ui);
  }
  const data = await listSchedules(ui.opts.productId);
  if (ui.state.dead) return;
  ui.state.loading = false;
  if (!data || data.error) {
    ui.state.fatal = (data && data.error) || 'Could not load schedules';
    ui.state.live = ui.state.fatal;
  } else {
    ui.state.fatal = '';
    ui.state.rows = data.schedules || [];
    ui.state.saved = data.saved_questions || [];
    const n = ui.state.rows.length;
    ui.state.live = n ? `${n} schedules` : 'No schedules';
  }
  paint(ui);
}

function mount(opts) {
  const root = el('div', { class: 'sched' });
  const ui = {
    opts,
    root,
    head: el('div'),
    main: el('div'),
    live: el('p', { class: 'sched-sr', 'aria-live': 'polite' }),
    state: {
      opts,
      rows: [],
      saved: [],
      shown: PAGE_SIZE,
      fatal: '',
      live: '',
      loading: true,
      busyId: '',
      dead: false,
      editor: null,
    },
  };
  ui.handlers = handlersFor(ui);
  root.append(ui.head, ui.main, ui.live);
  root.addEventListener('bonnie:unmount', () => {
    ui.state.dead = true;
    if (ui.state.editor) ui.state.editor.close();
  });
  load(ui, true);
  return root;
}

/**
 * Schedules for one product. GET /schedules has no limit, so the list
 * pages at 50 in the browser.
 * @param {{ productId: string, productName: string, me: object }} opts
 */
export function renderSchedules(opts) {
  if (!opts.productId) {
    return el('div', { class: 'sched' }, empty({
      title: 'Pick a product',
      body: 'Schedules belong to a product. Choose one in the top bar.',
    }));
  }
  return mount(opts);
}
